import { Prime100Coverages } from '../types';

export const PRIME_100_STANDARD_COVERAGE_MAPPING = Object.freeze({
  businessIncomeCoverage: Prime100Coverages.BI_EXTRA_EXPENSE,
  computerFundsTransfer: Prime100Coverages.COMPUTER_FUNDS_TRANSFER_FRAUD,
  extortionThreatsAndRansomPayment:
    Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT,
  hardwareReplCostEndorsement: Prime100Coverages.HARDWARE_REPLACEMENT_COSTS,
  postBreachRemediationEndorsement: Prime100Coverages.POST_BREACH_REMEDIATION,
  socialEngEndorsement: Prime100Coverages.SOCIAL_ENGINEERING,
  telecomsFraudEndorsement: Prime100Coverages.TELECOM_FRAUD,
  websiteMediaContentLiabilityEndorsement:
    Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY,
  isTriaExcluded: Prime100Coverages.TRIA,
  enhancedBusinessAndCbi:
    Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME,
  enhancedBusinessAndCbiWithSystemFailure:
    Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE,
  exclusionOfCbi: Prime100Coverages.EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME,
  reverseSocialEngEndorsement:
    Prime100Coverages.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT,
  dedicatedBreachCostsEndorsement:
    Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT,
  serviceFraudAndCryptojacking:
    Prime100Coverages.SERVICE_FRAUD_AND_CRYPTOJACKING,
  businessIncomeAndExtraExpenseSublimit:
    Prime100Coverages.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT,
  contingentBusinessIncomeSystemFailureItEndorsement:
    Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT,
  businessIncomeEcommerceAmendmentEndorsement:
    Prime100Coverages.BUSINESS_INCOME_ECOMMERCE_AMENDMENT,
  additionalDefenseExpensesLimitEndorsement:
    Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT,
  contingentBusinessIncomeSecurityFailureEndorsement:
    Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE,
  fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement:
    Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS,
  fundsTransferFraudAndSocialEngineeringEndorsement:
    Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
  contingentBusinessIncomeSystemFailureNonItEndorsement:
    Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_NON_IT,
  fundsTransferLiabilitySublimit:
    Prime100Coverages.FUNDS_TRANSFER_LIABILITY_SUBLIMIT,
  securityBreachLiability: Prime100Coverages.SECURITY_BREACH_LIABILITY,
  defenseAndSettlementAmendatoryEndorsement:
    Prime100Coverages.DEFENSE_AND_SETTLEMENT_AMENDATORY,
  reputationalHarmSublimit: Prime100Coverages.REPUTATIONAL_HARM_SUBLIMIT,
  publicRelationsExpense: Prime100Coverages.PUBLIC_RELATIONS_EXPENSE,
  invoiceManipulationEndorsement: Prime100Coverages.INVOICE_MANIPULATION,
  fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement:
    Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
  blanketAdditionalInsuredEndorsement:
    Prime100Coverages.BLANKET_ADDITIONAL_INSURED,
  courtAttendanceCosts: Prime100Coverages.COURT_ATTENDANCE_COSTS,
});

export const ELIGIBILITY_API_KEY_BY_COVERAGE_ID: Partial<
  Record<Prime100Coverages, string>
> = {
  ...(Object.fromEntries(
    (
      Object.entries(PRIME_100_STANDARD_COVERAGE_MAPPING) as [
        keyof typeof PRIME_100_STANDARD_COVERAGE_MAPPING,
        Prime100Coverages
      ][]
    ).map(([apiKey, coverageId]) => [coverageId, apiKey])
  ) as Partial<Record<Prime100Coverages, string>>),
  [Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY_V6]:
    'websiteMediaContentLiabilityEndorsement',
};

export type Prime100CoverageKeys =
  (typeof PRIME_100_STANDARD_COVERAGE_MAPPING)[keyof typeof PRIME_100_STANDARD_COVERAGE_MAPPING];

export const PRIME_100_COVERAGES = [
  {
    name: Prime100Coverages.BI_EXTRA_EXPENSE,
    isSelectedKey: 'businessIncomeCoverage',
    limitKey: 'businessIncomeCoverage',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.COMPUTER_FUNDS_TRANSFER_FRAUD,
    isSelectedKey: 'computerFraudEndorsement',
    limitKey: 'computerFraudLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.SOCIAL_ENGINEERING,
    isSelectedKey: 'socialEngEndorsement',
    limitKey: 'socialEngLimit',
    deductibleKey: 'socialEngDeductible',
  },
  {
    name: Prime100Coverages.EXTORTION_THREATS_AND_RANSOM_PAYMENT,
    isSelectedKey: 'ransomPaymentEndorsement',
    limitKey: 'ransomPaymentLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.PUBLIC_RELATIONS_EXPENSE,
    isSelectedKey: 'reputationalHarmSubLimit',
    limitKey: 'reputationalHarmSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.SECURITY_BREACH_LIABILITY,
    isSelectedKey: 'fundsTransferLiabilitySubLimit',
    limitKey: 'fundsTransferLiabilitySubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.HARDWARE_REPLACEMENT_COSTS,
    isSelectedKey: 'hardwareReplCostEndorsement',
    limitKey: 'hardwareReplCostSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.TELECOM_FRAUD,
    isSelectedKey: 'telecomsFraudEndorsement',
    limitKey: 'telecomsFraudSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.POST_BREACH_REMEDIATION,
    isSelectedKey: 'postBreachRemediationEndorsement',
    limitKey: 'postBreachRemediationSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY,
    isSelectedKey: 'websiteMediaContentLiabilityEndorsement',
    limitKey: 'websiteMediaContentLiabilitySubLimit',
    deductibleKey: 'websiteMediaContentLiabilityDeductible',
  },
  {
    name: Prime100Coverages.ADDITIONAL_INSURED,
    isSelectedKey: 'additionalInsureds',
    limitKey: 'websiteMediaContentLiabilitySubLimit',
    deductibleKey: '',
    payload: 'additionalInsureds',
  },
  {
    name: Prime100Coverages.TRIA,
    isSelectedKey: 'isTriaExcluded',
    payload: 'isTriaExcluded',
    deductibleKey: '',
    limitKey: '',
  },
  {
    name: Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME,
    isSelectedKey: 'enhancedBusinessAndCbi',
    limitKey: '',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE,
    isSelectedKey: 'enhancedBusinessAndCbiWithSystemFailure',
    limitKey: '',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.DEDICATED_BREACH_COSTS_ENDORSEMENT,
    isSelectedKey: '',
    limitKey: 'dedicatedBreachCostsEndorsementSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.SERVICE_FRAUD_AND_CRYPTOJACKING,
    isSelectedKey: '',
    limitKey: 'serviceFraudAndCryptojackingSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT,
    isSelectedKey: 'reverseSocialEngineeringEndorsement',
    limitKey: 'reverseSocialEngineeringLimit',
    deductibleKey: 'reverseSocialEngineeringDeductible',
  },
  {
    name: Prime100Coverages.ADDITIONAL_NAMED_INSURED,
    isSelectedKey: 'additionalNamedInsured',
    limitKey: '',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT,
    isSelectedKey: 'businessIncomeAndExtraExpenseSubLimit',
    limitKey: 'businessIncomeAndExtraExpenseSubLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE,
    isSelectedKey: 'contingentBusinessIncomeSecurityFailureEndorsement',
    limitKey: '',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT,
    isSelectedKey: 'contingentBusinessIncomeSystemFailureItEndorsement',
    limitKey: '',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT,
    isSelectedKey:
      'fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement',
    limitKey: 'fundsTransferFraudCoverageLimit',
    deductibleKey: 'fundsTransferFraudCoverageDeductible',
  },
  {
    name: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS,
    isSelectedKey:
      'fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement',
    limitKey: 'fundsTransferFraudCoverageLimit',
    deductibleKey: 'fundsTransferFraudCoverageDeductible',
  },
  {
    name: Prime100Coverages.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING,
    isSelectedKey: 'fundsTransferFraudAndSocialEngineeringEndorsement',
    limitKey: 'fundsTransferFraudCoverageLimit',
    deductibleKey: 'fundsTransferFraudCoverageDeductible',
  },
  {
    name: Prime100Coverages.INVOICE_MANIPULATION,
    isSelectedKey: 'invoiceManipulationEndorsement',
    limitKey: 'invoiceManipulationSubLimit',
    deductibleKey: 'invoiceManipulationDeductible',
  },
  {
    name: Prime100Coverages.BLANKET_ADDITIONAL_INSURED,
    isSelectedKey: 'blanketAdditionalInsuredEndorsement',
    limitKey: '',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.ADDITIONAL_DEFENSE_EXPENSES_LIMIT,
    isSelectedKey: 'additionalDefenseExpensesLimitEndorsement',
    limitKey: 'additionalDefenseExpensesLimit',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.COURT_ATTENDANCE_COSTS,
    isSelectedKey: 'courtAttendanceCosts',
    limitKey: 'courtAttendanceCosts',
    deductibleKey: '',
  },
  {
    name: Prime100Coverages.WEBSITE_MEDIA_CONTENT_LIABILITY_V6,
    isSelectedKey: 'websiteMediaContentLiabilityEndorsement',
    limitKey: 'websiteMediaContentLiabilitySubLimit',
    deductibleKey: 'websiteMediaContentLiabilityDeductible',
  },
];
